import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import _ from 'lodash'
import { CreditCard, DollarSign, Receipt, Settings } from 'lucide-react'
import { observer } from 'mobx-react'
import { useContext, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router'

import { DataTable } from '@/components/DataTable'
import { SortableColumnHeader } from '@/components/DataTable/DataTable'
import { Badge } from '@/components/elements/badge'
import { Button } from '@/components/elements/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/elements/card'
import { Input } from '@/components/elements/input'
import { getCreditBank, getCreditTransactions } from '@/services/credit_bank.service'
import { UserStoreContext } from '@/stores/UserStore'
import { formatCurrency, formatDateTime, toast } from '@/utils/helpers'

import AdjustModal from './AdjustModal'
import PurchaseModal from './PurchaseModal'

const CreditBank = observer(() => {
  const { entityId } = useParams()
  const { isXverseAdmin } = useContext(UserStoreContext)

  // State
  const [sorting, setSorting] = useState([{ id: 'created_at', desc: true }])
  const [pagination, setPagination] = useState({
    pageSize: 20,
    pageIndex: 0,
  })
  const [search, setSearch] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [showAdjustModal, setShowAdjustModal] = useState(false)
  const [showPurchaseModal, setShowPurchaseModal] = useState(false)

  // Data
  const queryClient = useQueryClient()
  const {
    data: creditBank,
    isPending: isPendingBank,
    isError: isErrorBank,
  } = useQuery({
    queryKey: ['creditBank', entityId],
    queryFn: () => getCreditBank(entityId),
    enabled: !!entityId,
    retry: false,
  })

  const { data: transactions, isPending: isPendingTransactions } = useQuery({
    queryKey: ['creditTransactions', entityId, sorting, pagination, searchTerm],
    queryFn: () => getCreditTransactions(entityId, sorting, pagination, searchTerm || null),
    placeholderData: keepPreviousData,
    enabled: !!entityId && !isErrorBank,
  })

  // Search shrinks the result set, so a deep page can 404 from the paginator.
  // Reset to page 0 in the same handler so React batches the state updates.
  const resetToFirstPage = () =>
    setPagination((prev) => (prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }))

  const debounceSearch = useRef(
    _.debounce((value) => {
      setSearchTerm(value)
      resetToFirstPage()
    }, 500),
  ).current

  // Handlers
  const handleAdjustSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['creditBank'] })
    queryClient.invalidateQueries({ queryKey: ['creditTransactions'] })
    setShowAdjustModal(false)
    toast('Balance adjusted successfully', 'success')
  }

  const handlePurchaseSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['creditBank'] })
    queryClient.invalidateQueries({ queryKey: ['creditTransactions'] })
    setShowPurchaseModal(false)
  }

  // Transaction type badge config
  const getTransactionTypeBadge = (type) => {
    const config = {
      Recharge: { variant: 'success', label: 'Recharge' },
      Deduction: { variant: 'default', label: 'Deduction' },
      Adjustment: { variant: 'warning', label: 'Adjustment' },
    }
    return config[type] || { variant: 'default', label: type }
  }

  // Table columns
  const columns = useMemo(
    () => [
      {
        accessorKey: 'transactionType',
        header: 'Type',
        cell: ({ row }) => {
          const config = getTransactionTypeBadge(row.original.transactionType)
          return <Badge variant={config.variant}>{config.label}</Badge>
        },
        minSize: 135,
        maxSize: 160,
      },
      {
        accessorKey: 'amount',
        header: ({ column }) => <SortableColumnHeader column={column} label="Amount" />,
        cell: ({ row }) => {
          const amount = parseFloat(row.original.amount)
          const isPositive = amount > 0
          return (
            <span className={isPositive ? 'text-green-600' : 'text-red-600'}>
              {isPositive ? '+' : ''}
              {formatCurrency(amount)}
            </span>
          )
        },
        minSize: 135,
        maxSize: 160,
      },
      {
        accessorKey: 'balance_after',
        header: ({ column }) => <SortableColumnHeader column={column} label="Balance After" />,
        cell: ({ row }) => formatCurrency(row.original.balanceAfter),
        minSize: 170,
        maxSize: 190,
      },
      {
        accessorKey: 'notes',
        header: 'Notes',
        cell: ({ row }) => {
          const reference = row.original.metadata?.reference
          const notes = row.original.notes || '—'

          return (
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground max-w-md truncate">{notes}</span>
                {row.original.receiptUrl && (
                  <a
                    href={row.original.receiptUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:text-primary/80 inline-flex items-center gap-1 text-sm"
                  >
                    <Receipt className="h-4 w-4" />
                    Receipt
                  </a>
                )}
              </div>
              {reference && (
                <span className="text-muted-foreground text-xs" title={reference}>
                  {' '}
                  Ref: {reference.length > 50 ? `${reference.substring(0, 50)}...` : reference}
                </span>
              )}
            </div>
          )
        },
        minSize: 380,
        maxSize: 600,
      },
      {
        accessorKey: 'created_at',
        header: ({ column }) => <SortableColumnHeader column={column} label="Date" />,
        cell: ({ row }) => formatDateTime(row.original.createdAt),
        minSize: 180,
      },
    ],
    [],
  )

  return (
    <div className="flex flex-col gap-4">
      {/* Error State */}
      {isErrorBank && (
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-destructive text-lg font-semibold">Credit Bank Not Found</p>
              <p className="text-muted-foreground mt-2 text-sm">
                This account does not have a credit bank yet. Please contact RescueX support.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Credit Bank Overview Card */}
      {!isErrorBank && (
        <Card className="mb-6">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <div>
              <CardTitle className="text-2xl font-bold">Current Balance</CardTitle>
              <CardDescription>Available credits for certificate assignments</CardDescription>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => setShowPurchaseModal(true)}
                disabled={isPendingBank}
                variant="default"
              >
                <CreditCard className="mr-2 h-4 w-4" />
                Purchase Credits
              </Button>
              {isXverseAdmin && (
                <Button
                  onClick={() => setShowAdjustModal(true)}
                  disabled={isPendingBank}
                  variant="outline"
                >
                  <Settings className="mr-2 h-4 w-4" />
                  Adjust Balance
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {isPendingBank ? (
              <div className="bg-muted h-12 animate-pulse rounded" />
            ) : (
              <div className="flex items-center gap-4">
                <div className="bg-primary/10 flex h-16 w-16 items-center justify-center rounded-full">
                  <DollarSign className="text-primary h-8 w-8" />
                </div>
                <div>
                  <p className="text-4xl font-bold">{formatCurrency(creditBank?.balance || 0)}</p>
                  <p className="text-muted-foreground text-sm">
                    Last updated {formatDateTime(creditBank?.modifiedAt)}
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Transaction History */}
      {!isErrorBank && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Transaction History</CardTitle>
                <CardDescription>View all credit transactions for this account</CardDescription>
              </div>
              <div className="w-64">
                <Input
                  placeholder="Search transactions..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value)
                    debounceSearch(e.target.value)
                  }}
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <DataTable
              columns={columns}
              data={transactions?.results || []}
              rowCount={transactions?.count || 0}
              loading={isPendingTransactions}
              sorting={sorting}
              setSorting={setSorting}
              pagination={pagination}
              setPagination={setPagination}
            />
          </CardContent>
        </Card>
      )}

      {/* Purchase Modal */}
      {showPurchaseModal && (
        <PurchaseModal
          isOpen={showPurchaseModal}
          onClose={() => setShowPurchaseModal(false)}
          onSuccess={handlePurchaseSuccess}
        />
      )}

      {/* Adjust Modal (RescueX Admins only) */}
      {showAdjustModal && (
        <AdjustModal
          isOpen={showAdjustModal}
          onClose={() => setShowAdjustModal(false)}
          onSuccess={handleAdjustSuccess}
          entityId={entityId}
        />
      )}
    </div>
  )
})

export default CreditBank
